import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { duration } from "@formant/core/model/duration";
import { IDeviceTeleopConfiguration } from "@formant/core/model/IDeviceTeleopConfiguration";
import { IRtcStream } from "@formant/core/model/IRtcStream";
import { rosTypeJoy } from "@formant/core/model/rosTypes";
import { rtcJoyStreamType } from "@formant/core/model/rtcStreamTypes";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";
import { createRtcStreamMessage } from "@formant/rtc-client/utils/createRtcStreamMessage";
import { WindowEvent } from "@formant/ui/component/common/WindowEvent";

import { track } from "../../../../analytics";
import { operateStore } from "../../../../stores";
import { ITeleopSession } from "../../../../teleop/ITeleopSession";
import { StandardButton } from "../../../common/StandardButton";
import { Timer } from "../../../common/Timer";
import * as styles from "./TeleopCommandButton.scss";

export interface ITeleopCommandButtonProps {
    session: ITeleopSession;
    stream: IRtcStream;
    index: number;
    disabled?: boolean;
    statusTopic?: string;
    statusLabel?: string;
    backgroundColor?: string;
    disableGamepadInput?: boolean;
    disableKeyboardInput?: boolean;
    configuration?: IDeviceTeleopConfiguration;
}

@observer
export class TeleopCommandButton extends Component<ITeleopCommandButtonProps> {
    @observable
    private mouseDownValue = false;
    @observable
    private value = false;
    private unregister?: () => boolean;

    private disableGamepadForwarding?: boolean;

    public componentDidMount() {
        const { statusTopic, configuration } = this.props;
        if (statusTopic) {
            this.unregister = this.props.session.registerReceiver(
                statusTopic,
                this.onReceiveStatus,
            );
        }
        this.disableGamepadForwarding = (
            (configuration?.rosStreams || []).find(
                _ => _.topicType !== rosTypeJoy,
            ) ||
            (configuration?.customStreams || []).find(
                _ => _.rtcStreamType !== rtcJoyStreamType,
            )
        )?.joyDisableButtonMapping;
    }

    public componentWillUnmount() {
        if (this.unregister) {
            this.unregister();
        }
    }

    public render() {
        const {
            stream,
            disabled,
            statusTopic,
            backgroundColor,
            disableGamepadInput,
        } = this.props;
        const { mouseDownValue, onMouseDown, onMouseUp } = this;
        if (!stream) {
            return false;
        }

        const concatName =
            stream.buttonLabel ||
            stream.label ||
            stream.streamName.split("/").pop();

        return (
            <StandardButton
                className={classnames(
                    styles["teleop-command-button"],
                    backgroundColor &&
                        styles["teleop-command-button-" + backgroundColor],
                    mouseDownValue && styles.active,
                    disabled && styles.disabled,
                    statusTopic && styles.status,
                )}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                disabled={disabled}
                data-testid="teleop-command-button"
            >
                {statusTopic && (
                    <div
                        className={classnames(
                            styles["status-chip"],
                            styles[this.value.toString()],
                        )}
                        data-tooltip={`${statusTopic}: ${this.value.toString()}`}
                    />
                )}
                {!disabled && !disableGamepadInput && (
                    <Timer
                        interval={duration.second * 0.05}
                        onTick={this.handleGamepad}
                    />
                )}
                {concatName}
                {!disabled && (
                    <>
                        <WindowEvent type="keydown" onEvent={this.onKeyDown} />
                        <WindowEvent type="keyup" onEvent={this.onKeyUp} />
                    </>
                )}
            </StandardButton>
        );
    }

    public handleGamepad = () => {
        const { index, disableGamepadInput } = this.props;
        const gamepad = operateStore.gamepad;

        if (!gamepad || disableGamepadInput || this.disableGamepadForwarding) {
            return;
        }

        const button =
            gamepad.buttons.length > index ? gamepad.buttons[index] : undefined;
        if (button && button.pressed) {
            this.onMouseDown();
        } else if (button && !button.pressed) {
            this.onMouseUp();
        }
    };

    private set mouseDown(value: boolean) {
        if (this.mouseDownValue !== value) {
            this.mouseDownValue = value;
            this.sendCommand(this.mouseDownValue);
        }
    }

    private onMouseDown = () => {
        track("Teleop Button pressed");
        this.mouseDown = true;
    };

    private onMouseUp = () => {
        this.mouseDown = false;
    };

    private handleKeyboardEvent = (
        event: KeyboardEvent,
        isKeyDown: boolean,
    ) => {
        const { disableKeyboardInput } = this.props;
        if (event.target instanceof HTMLInputElement || disableKeyboardInput) {
            return;
        }
        const { shiftKey, altKey, code } = event;
        const numberMatch = code.match(/\d/);

        if (!numberMatch) {
            return;
        }

        const numberPressed = numberMatch[0];
        const { index } = this.props;
        const targetNumber = (index + 1) % 10;
        const isShift = index >= 10 && index < 20;
        const isShiftAlt = index >= 20 && index < 30;

        const isValidKeyPress =
            (!isShiftAlt && !isShift && !shiftKey && !altKey) ||
            (isShift && shiftKey && !altKey) ||
            (isShiftAlt && shiftKey && altKey);

        if (numberPressed === `${targetNumber}` && isValidKeyPress) {
            this.mouseDown = isKeyDown;
        }
    };

    private onKeyDown = (event: KeyboardEvent) => {
        this.handleKeyboardEvent(event, true);
    };

    private onKeyUp = (event: KeyboardEvent) => {
        this.handleKeyboardEvent(event, false);
    };

    private sendCommand = (value: boolean) => {
        const { session, stream } = this.props;
        if (stream) {
            session.send(
                createRtcStreamMessage(
                    stream,
                    stream.label
                        ? {
                              bitset: {
                                  bits: [{ key: stream.label, value }],
                              },
                          }
                        : {
                              boolean: value,
                          },
                ),
            );
        }
    };

    private onReceiveStatus = (msg: IRtcStreamMessage) => {
        if (msg.payload.boolean !== undefined) {
            this.value = msg.payload.boolean;
        } else if (msg.payload.bitset !== undefined) {
            for (const bit of msg.payload.bitset.bits) {
                if (bit.key === this.props.statusLabel) {
                    this.value = bit.value || false;
                }
            }
        }
    };
}
